import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext();

export const LANG_STORAGE_KEY = "viniport-lang";

export function getPreferredLanguage() {
  if (typeof window === "undefined") return "pt";
  const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
  if (stored === "pt" || stored === "en") return stored;
  return typeof navigator !== "undefined" && navigator.language?.startsWith("en")
    ? "en"
    : "pt";
}

const translations = {
  pt: {
    nav: {
      about: "Sobre",
      experience: "Experiência",
      projects: "Projetos",
      contact: "Contato",
      resume: "Currículo",
    },
    hero: {
      status: "Goiás, Brasil · Remoto",
      title: "Engenheiro Backend construindo sistemas confiáveis e escaláveis para plataformas financeiras.",
      description:
        "Desenvolvo APIs de baixa latência e microsserviços orientados a eventos em Go, sobre arquitetura serverless na AWS. Também atuo fullstack, unindo sistemas de dados complexos a interfaces bem construídas.",
      ctaWork: "Ver projetos",
      ctaContact: "Entrar em contato",
    },
    about: {
      eyebrow: "Sobre",
      title: "Vinícius Gabriel",
      subtitle: "Software Engineer · Especialista em Back-end · Cientista da Computação",
      location: "Goiás, Brasil",
      text1:
        "Mestre em Ciência da Computação com mais de 4 anos construindo sistemas de alta performance. Especialista em Go e infraestrutura AWS, com histórico em microsserviços escaláveis e arquiteturas serverless (Lambda, DynamoDB, ECS).",
      text2:
        "Minha experiência fullstack me permite conectar sistemas de dados complexos a experiências de usuário sólidas, sempre com foco em confiabilidade e performance.",
      educationTitle: "Formação",
      masters: "Mestrado em Ciência da Computação",
      bachelors: "Bacharelado em Ciência da Computação",
      languagesTitle: "Idiomas",
      languages: [
        { name: "Português", level: "Nativo" },
        { name: "Inglês", level: "Avançado" },
        { name: "Espanhol", level: "Básico" },
      ],
    },
    experience: {
      eyebrow: "Experiência",
      title: "Experiência Profissional",
      items: [
        {
          role: "Backend Engineer",
          company: "Deal · Nubank",
          meta: "Remoto",
          period: "Jul 2026 — Atual",
          bullets: [
            "Atuação no time de engenharia da Nubank via Deal Technologies, com Clojure",
          ],
          stack: ["Clojure"],
        },
        {
          role: "Backend Engineer",
          company: "TC Traders Club",
          meta: "Remoto · São Paulo, SP",
          period: "Mai 2025 — Dez 2025",
          bullets: [
            "APIs e serviços back-end em Go para plataforma financeira de alta disponibilidade e baixa latência",
            "Arquitetura serverless e de microsserviços na AWS: Lambda, Timestream, DynamoDB, ECS e EC2",
            "Integração front-end/back-end para dados financeiros em tempo real; modelagem de bancos SQL e NoSQL e containerização com Docker",
          ],
          stack: ["Go", "AWS Lambda", "Timestream", "DynamoDB", "Docker"],
        },
        {
          role: "Full Stack Engineer",
          company: "Dank City Delivery",
          meta: "Remoto · Sacramento, CA - EUA",
          period: "Fev 2024 — Mai 2025",
          bullets: [
            "Stack inicial com Next.js, React, Redis e StoryBlok, com deploy na Vercel",
            "Configuração de ambiente customizado com Nginx, Pm2, MongoDB e GitHub Actions para suportar aplicações mais robustas e rápidas conforme o projeto evoluiu",
            "Melhoria de 30% na performance do sistema através de otimizações técnicas",
          ],
          stack: ["Next.js", "React", "MongoDB", "Redis", "GitHub Actions"],
        },
        {
          role: "Desenvolvedor Back-End",
          company: "Nelogica",
          meta: "Remoto · Porto Alegre, RS",
          period: "Mar 2022 — Fev 2024",
          bullets: [
            "Desenvolvimento de soluções de alta performance para o mercado financeiro brasileiro, com foco na integração com corretoras, bancos e bolsas de valores",
            "Aumento de 25% na eficiência das transações através de otimização de sistemas; monitoramento de microsserviços e integração de bancos de dados entre múltiplos serviços",
            "Migração e refatoração de código legado em Delphi e Java; conexões WebSocket, integrações REST, threads e multiprocessamento; TDD e CI/CD",
          ],
          stack: ["Delphi", "Java", "WebSockets", "Microservices", "CI/CD"],
        },
      ],
    },
    projects: {
      eyebrow: "Projetos",
      title: "Selected Projects",
      intro:
        "Uma seleção dos projetos pessoais mais relevantes. O restante está disponível no GitHub.",
      moreTitle: "Outros projetos",
      moreLinkText: "Ver todos no GitHub",
    },
    expertise: {
      eyebrow: "Expertise",
      title: "Áreas de atuação",
      categories: {
        backend: "Backend",
        cloud: "Cloud & Infraestrutura",
        data: "Dados",
        frontend: "Frontend",
        ai: "Ferramentas de IA",
      },
    },
    contact: {
      eyebrow: "Contato",
      title: "Interessado em trabalhar juntos?",
      text: "Estou aberto a novas oportunidades, conversas sobre tecnologia e colaborações.",
      ctaLabel: "Enviar email",
      email: "Email",
      linkedIn: "LinkedIn",
      github: "GitHub",
      location: "Goiás, Brasil",
    },
    footer: {
      tagline: "Feito com React · Hospedado na Vercel",
    },
    resume: {
      title: "Currículo",
      subtitle: "Acesse meu currículo em tempo real, em Português e Inglês",
      redirectingText:
        "Você será redirecionado para o PDF do currículo. Se o redirecionamento não ocorrer, use o link abaixo.",
      resumeLinkText: "Abrir currículo em PDF",
      back: "Voltar para o início",
    },
  },
  en: {
    nav: {
      about: "About",
      experience: "Experience",
      projects: "Projects",
      contact: "Contact",
      resume: "Resume",
    },
    hero: {
      status: "Goiás, Brazil · Remote",
      title: "Backend Engineer building reliable, scalable systems for financial platforms.",
      description:
        "I build low-latency APIs and event-driven microservices in Go, backed by serverless architecture on AWS. I also work fullstack, connecting complex data systems to well-built interfaces.",
      ctaWork: "View my work",
      ctaContact: "Get in touch",
    },
    about: {
      eyebrow: "About",
      title: "Vinícius Gabriel",
      subtitle: "Software Engineer · Back-end Specialist · Computer Scientist",
      location: "Goiás, Brazil",
      text1:
        "Master's in Computer Science with over 4 years building high-performance systems. Specialist in Go and AWS infrastructure, with a track record in scalable microservices and serverless architectures (Lambda, DynamoDB, ECS).",
      text2:
        "My fullstack background lets me bridge complex data systems and polished user experiences, always with an eye on reliability and performance.",
      educationTitle: "Education",
      masters: "Master's in Computer Science",
      bachelors: "Bachelor's in Computer Science",
      languagesTitle: "Languages",
      languages: [
        { name: "Portuguese", level: "Native" },
        { name: "English", level: "Advanced" },
        { name: "Spanish", level: "Basic" },
      ],
    },
    experience: {
      eyebrow: "Experience",
      title: "Work Experience",
      items: [
        {
          role: "Backend Engineer",
          company: "Deal · Nubank",
          meta: "Remote",
          period: "Jul 2026 — Present",
          bullets: [
            "Working on Nubank's engineering team through Deal Technologies, using Clojure",
          ],
          stack: ["Clojure"],
        },
        {
          role: "Backend Engineer",
          company: "TC Traders Club",
          meta: "Remote · São Paulo, SP",
          period: "May 2025 — Dec 2025",
          bullets: [
            "Developed APIs and back-end services in Go for a financial platform with high availability and low latency",
            "Implemented serverless and microservices-based architecture on AWS: Lambda, Timestream, DynamoDB, ECS, and EC2",
            "Front-end/back-end integration for real-time financial data; SQL and NoSQL database modeling and containerization with Docker",
          ],
          stack: ["Go", "AWS Lambda", "Timestream", "DynamoDB", "Docker"],
        },
        {
          role: "Full Stack Engineer",
          company: "Dank City Delivery",
          meta: "Remote · Sacramento, CA - USA",
          period: "Feb 2024 — May 2025",
          bullets: [
            "Started with a Next.js, React, Redis, and StoryBlok stack, deployed on Vercel",
            "Set up a custom environment with Nginx, Pm2, MongoDB, and GitHub Actions to support more powerful and faster applications as the project grew",
            "Improved system performance by 30% through technical optimizations",
          ],
          stack: ["Next.js", "React", "MongoDB", "Redis", "GitHub Actions"],
        },
        {
          role: "Back-End Developer",
          company: "Nelogica",
          meta: "Remote · Porto Alegre, RS",
          period: "Mar 2022 — Feb 2024",
          bullets: [
            "Built high-performance solutions for the Brazilian financial market, focused on integration with brokers, banks, and stock exchanges",
            "Increased transaction efficiency by 25% through system optimization; monitored microservices health and database integration across services",
            "Migrated and refactored legacy code in Delphi and Java; WebSocket connections, REST integrations, threading, and multiprocessing; TDD and CI/CD",
          ],
          stack: ["Delphi", "Java", "WebSockets", "Microservices", "CI/CD"],
        },
      ],
    },
    projects: {
      eyebrow: "Projects",
      title: "Selected Projects",
      intro:
        "A selection of the most relevant personal projects. The rest are available on GitHub.",
      moreTitle: "Other projects",
      moreLinkText: "View all on GitHub",
    },
    expertise: {
      eyebrow: "Expertise",
      title: "Areas of expertise",
      categories: {
        backend: "Backend",
        cloud: "Cloud & Infrastructure",
        data: "Data",
        frontend: "Frontend",
        ai: "AI Tooling",
      },
    },
    contact: {
      eyebrow: "Contact",
      title: "Interested in working together?",
      text: "I'm open to new opportunities, tech conversations, and collaborations.",
      ctaLabel: "Send an email",
      email: "Email",
      linkedIn: "LinkedIn",
      github: "GitHub",
      location: "Goiás, Brazil",
    },
    footer: {
      tagline: "Built with React · Hosted on Vercel",
    },
    resume: {
      title: "Resume",
      subtitle: "Open my resume PDF in English or Portuguese",
      redirectingText:
        "You will be redirected to the resume PDF. If that does not happen, use the link below.",
      resumeLinkText: "Open resume PDF",
      back: "Back to home",
    },
  },
};

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const segments = location.pathname.split("/").filter(Boolean);
  const lang = segments[0] === "en" ? "en" : "pt";

  useEffect(() => {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    document.documentElement.lang = lang === "pt" ? "pt-BR" : "en";
  }, [lang]);

  const navigateToLang = (nextLang) => {
    const rest = segments.slice(1).join("/");
    navigate(`/${nextLang}${rest ? `/${rest}` : ""}${location.hash}`);
  };

  const toggleLanguage = () => {
    navigateToLang(lang === "pt" ? "en" : "pt");
  };

  const setLanguage = (language) => {
    if (language === "pt" || language === "en") {
      navigateToLang(language);
    }
  };

  const value = useMemo(
    () => ({ lang, toggleLanguage, setLanguage, t: translations[lang] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, location.pathname, location.hash]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
